import { NavArrows } from "@components/atoms/NavArrows"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Slider } from "@components/atoms/Slider"
import { FilmTile } from "@components/film/FilmTile"
import { Chip } from "@components/ui/Chip"
import { trackEvent } from "@features/tracking/tracking"
import { GuestListingItemFragment } from "@generated-types"
import { BreakpointName, useBreakpoint } from "@hooks/useBreakpoint"
import { cn } from "@utils/tw"
import { defineBlock, EditableText } from "eddev/blocks"
import { useRoute } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useKeyPressEvent } from "react-use"

export const meta: BlockMeta = {
  title: "Guest Listing",
  icon: "admin-users",
  tags: ["#subpage-layout"],
  description: "Displays a list of guests.",
  flags: {
    fullWidth: true,
  },
}

const RESULTS_PER_ROW: { [K in BreakpointName]: number } = {
  BASE: 2,
  sm: 2,
  md: 4,
  lg: 6,
  xl: 6,
  xxl: 6,
}

export default defineBlock("guests/guest-listing", (props) => {
  const ref = useRef<HTMLDivElement>(null)
  const guests = props.currentProgram?.guests ?? []
  const breakpoint = useBreakpoint()
  const perRow = RESULTS_PER_ROW[breakpoint]

  const [previewIndex, setPreviewIndex] = useState(-1)

  const hash = useRoute().hash

  useEffect(() => {
    if (!hash) return
    const index = guests.findIndex((guest) => guest?.slug === hash.replace("#", ""))
    if (index >= 0) {
      setPreviewIndex(index)
    }
  }, [hash])

  const contents = useMemo(() => {
    const items = guests?.map((guest, index) => {
      return (
        <div key={index} className="col-span-6 md:col-span-3 lg:col-span-2">
          <GuestTile
            {...guest!}
            active={previewIndex === index}
            onClick={() => {
              if (index === previewIndex) {
                setPreviewIndex(-1)
              } else {
                setPreviewIndex(index)
                trackEvent(
                  "Guest Tile Click",
                  {
                    Guest: guest?.title,
                  },
                  ref.current,
                )
              }
            }}
          />
        </div>
      )
    })

    const previewRow = Math.floor(previewIndex / perRow)
    const previewDisplayIndex = (previewRow + 1) * perRow
    if (previewIndex >= 0) {
      items.splice(
        previewDisplayIndex,
        0,
        <PreviewBox
          key="preview"
          guest={guests![previewIndex]!}
          closed={previewIndex < 0}
          hasPrevious={previewIndex > 0}
          hasNext={previewIndex < guests.length - 1}
          onGo={(direction) => {
            setPreviewIndex(previewIndex + direction)
          }}
          onClose={() => {
            setPreviewIndex(-1)
          }}
        />,
      )
    }

    return items
  }, [perRow, previewIndex])

  return (
    <div data-mixpanel-block="Guest Listing" ref={ref}>
      <div className="type-title-m w-grid mx-auto">
        <EditableText store="title" defaultValue="Guests A-Z" />
      </div>
      <div className="grid-auto">{contents}</div>
    </div>
  )
})

type GuestTileProps = GuestListingItemFragment & {
  onClick?: () => void
  active?: boolean
}

function GuestTile(props: GuestTileProps) {
  return (
    <a
      className={cn(
        "flex flex-col gap-3 hover-card group cursor-pointer pb-6 mt-4",
        props.active && "hover-card-previewing",
      )}
      onClick={props.onClick}
    >
      <div className="aspect-square relative overflow-hidden transition-[border-radius] duration-150 group-hover:rounded-md [.hover-card-previewing_&]:rounded-md">
        <ResponsiveImage {...props.guestInfo?.headshot} max />
      </div>
      <div className="flex flex-col gap-1">
        <div className="type-ui-m">{props.title}</div>
        <div>
          {props.guestInfo?.role && (
            <Chip shape="rounded" variant="high" className="pointer-events-none">
              {props.guestInfo?.role}
            </Chip>
          )}
        </div>
      </div>
    </a>
  )
}

type PreviewBoxProps = {
  guest: GuestListingItemFragment
  closed: boolean
  hasPrevious: boolean
  hasNext: boolean
  onGo: (direction: number) => void
  onClose: () => void
}

function PreviewBox(props: PreviewBoxProps) {
  const ref = useRef<HTMLDivElement>(null!)
  const isFirst = useRef(true)

  if (props.closed) isFirst.current = true

  useLayoutEffect(() => {
    if (!ref.current) return
    let { top, height } = ref.current.getBoundingClientRect()
    // console.log({ top, height })

    const windowHeight = window.innerHeight
    const headerHeight = document.getElementById("site-header")?.clientHeight ?? 0
    const scrollTop = document.scrollingElement?.scrollTop!
    const scrollTarget = scrollTop + (top - (windowHeight - headerHeight) / 2 + height / 2) - headerHeight

    if (isFirst.current) {
      setTimeout(() => {
        document.scrollingElement?.scrollTo({
          top: scrollTarget,
          behavior: "smooth",
        })
      }, 100)
    } else {
      document.scrollingElement?.scrollTo({
        top: scrollTarget,
        behavior: "instant",
      })
    }
    isFirst.current = false
  }, [props.guest])

  useKeyPressEvent("Escape", (e) => {
    e.preventDefault()
    props.onClose()
  })

  return (
    <div
      className="col-span-full relative theme-dark flex justify-center"
      data-mixpanel-component="Guest Preview"
      data-mixpanel-guest={props.guest.title}
    >
      <AnimatePresence>
        {!props.closed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden w-[100vw] mx-auto flex-none"
          >
            <div ref={ref} className="pb-6 pt-1 will-change-transform w-grid mx-auto">
              <div className="bg-black text-fg rounded-lg">
                <Slider
                  key={props.guest.id}
                  arrows={false}
                  scrollerClass="flex gap-grid-gutter px-grid-gutter scroll-mask-start-0 !scroll-mask-inset-0"
                >
                  {/* Info */}
                  <div className="py-grid-gutter pl-3 flex flex-col flex-none gap-5 scroll-mx-grid-gutter w-cols-8 md:w-cols-6 lg:w-cols-5">
                    <div className="type-title-xl pt-3">{props.guest.title}</div>
                    <div className="flex gap-5">
                      {props.guest.guestInfo?.role && (
                        <Chip shape="rounded" variant="high" interactive={false}>
                          {props.guest.guestInfo?.role}
                        </Chip>
                      )}
                      {props.guest.films?.length! > 0 && (
                        <span className="type-label-s">
                          Attending {props.guest.films?.length} {props.guest.films?.length === 1 ? "event" : "events"}
                        </span>
                      )}
                    </div>
                    <div className="type-body-m text-pretty">
                      <p dangerouslySetInnerHTML={{ __html: props.guest.guestInfo?.bio! }} />
                    </div>
                  </div>
                  {/* Films */}
                  {props.guest.films?.map((film, index) => {
                    return (
                      <div key={index} className="w-cols-7 md:w-cols-4 lg:w-cols-3 flex-none py-6">
                        <FilmTile film={film!} />
                      </div>
                    )
                  })}
                </Slider>
              </div>
              <div className="hidden md:block">
                <NavArrows
                  canGoLeft={props.hasPrevious}
                  canGoRight={props.hasNext}
                  onLeft={() => props.onGo(-1)}
                  onRight={() => props.onGo(1)}
                  useKeyNav
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
